import { YouTubeTranscriptItem, YouTubeVideoInfo, fetchYouTubeData } from './youtube-service';

export interface YouTubeContext {
  videoId: string;
  title: string;
  channelTitle: string;
  currentTime: number;
  thumbnail: string;
  transcriptSnippet: string;
  nearbyTranscript: YouTubeTranscriptItem[];
}

/**
 * Get the best available thumbnail URL for a YouTube video
 */
export function getYouTubeThumbnail(videoId: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'high'): string {
  const thumbnailUrls = {
    default: `https://img.youtube.com/vi/${videoId}/default.jpg`, // 120x90
    medium: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`, // 320x180
    high: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`, // 480x360
    standard: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`, // 640x480
    maxres: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` // 1280x720
  };
  
  return thumbnailUrls[quality];
}

/**
 * Extract transcript snippet around a specific time with context window
 */
export function getTranscriptSnippet(
  transcript: YouTubeTranscriptItem[], 
  currentTime: number, 
  windowSeconds: number = 15
): { snippet: string; nearbyItems: YouTubeTranscriptItem[] } {
  if (!transcript || transcript.length === 0) {
    return { snippet: '', nearbyItems: [] };
  }

  // Find items within the time window (±15 seconds)
  const startTime = Math.max(0, currentTime - windowSeconds);
  const endTime = currentTime + windowSeconds;
  
  const nearbyItems = transcript.filter(item => {
    const itemEnd = item.start + item.duration;
    return (item.start <= endTime && itemEnd >= startTime);
  });

  // Create a readable snippet
  const snippet = nearbyItems
    .map(item => item.text)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return { snippet, nearbyItems };
}

/**
 * Create YouTube context object for sending to the agent
 */
export async function createYouTubeContext(
  videoId: string, 
  currentTime: number,
  videoInfo?: YouTubeVideoInfo
): Promise<YouTubeContext | null> {
  try {
    // Fetch combined YouTube data once (transcript + video info)
    const youtubeData = await fetchYouTubeData(videoId).catch(() => ({
      transcript: [],
      video_info: videoInfo || { title: 'Unknown Title', description: '', duration: '', channelTitle: 'Unknown Channel' },
      chapters: [],
      has_chapters: false,
      full_text: '',
      language: 'en',
      total_segments: 0,
    } as any));

    const info = videoInfo || youtubeData.video_info;
    const { snippet, nearbyItems } = getTranscriptSnippet(youtubeData.transcript || [], currentTime);

    return {
      videoId,
      title: info.title,
      channelTitle: info.channelTitle,
      currentTime,
      thumbnail: getYouTubeThumbnail(videoId, 'high'),
      transcriptSnippet: snippet,
      nearbyTranscript: nearbyItems
    };
  } catch (error) {
    console.error('Failed to create YouTube context:', error);
    return null;
  }
}

/**
 * Format YouTube context for sending to the agent
 */
export function formatYouTubeContextMessage(context: YouTubeContext): string {
  const timeFormatted = formatTime(context.currentTime);
  
  return `📺 YouTube Video Context:
Title: ${context.title}
Channel: ${context.channelTitle}
Current Time: ${timeFormatted}
Transcript: ${context.transcriptSnippet || 'No transcript available at this time'}`;
}

/**
 * Format time in MM:SS or HH:MM:SS format
 */
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}
